// Shared punctuality verdict logic. Both Punctuality list and LessonDetail
// must reach the same conclusion for the same session.
//
// Data shape note: the `sessions` table stores tutor lateness in
// `tutor_join_lateness_seconds` (unsigned, tutor only) and a signed
// `lateness_seconds` where positive = tutor late, negative = student late.
// There is no separate `student_join_lateness_seconds` column.

export const DEFAULT_THRESHOLDS = { minor: 3, moderate: 6, severe: 10 };

export function getTutorLatenessSeconds(session) {
  if (!session) return 0;
  if (session.tutor_join_lateness_seconds && session.tutor_join_lateness_seconds > 0) {
    return session.tutor_join_lateness_seconds;
  }
  if (session.lateness_seconds && session.lateness_seconds > 0) {
    return session.lateness_seconds;
  }
  return 0;
}

export function getStudentLatenessSeconds(session) {
  if (!session) return 0;
  if (session.lateness_seconds && session.lateness_seconds < 0) {
    return Math.abs(session.lateness_seconds);
  }
  return 0;
}

function severityFor(minutes, thresholds) {
  const t = thresholds || DEFAULT_THRESHOLDS;
  if (minutes >= t.severe) return 'severe';
  if (minutes >= t.moderate) return 'moderate';
  if (minutes >= t.minor) return 'minor';
  return null;
}

// Returns the verdict for a session. `status` is 'late' only when the larger
// of tutor/student lateness clears the configured minor threshold.
export function getPunctualityVerdict(session, thresholds) {
  const t = thresholds || DEFAULT_THRESHOLDS;
  const tutorSec = getTutorLatenessSeconds(session);
  const studentSec = getStudentLatenessSeconds(session);

  const tutorMin = tutorSec / 60;
  const studentMin = studentSec / 60;

  const who = tutorSec >= studentSec ? 'tutor' : 'student';
  const seconds = who === 'tutor' ? tutorSec : studentSec;
  const minutes = seconds / 60;
  const severity = severityFor(minutes, t);

  return {
    status: severity ? 'late' : 'on_time',
    who,
    seconds,
    minutes,
    severity,
    tutorSeconds: tutorSec,
    studentSeconds: studentSec,
    tutorAboveThreshold: tutorMin >= t.minor,
    studentAboveThreshold: studentMin >= t.minor,
  };
}

export function formatLateness(seconds) {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
}
